import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  DialogContentText,
  InputAdornment,
  Tooltip
} from '@mui/material';
import {
  Info as InfoIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  Print as PrintIcon,
  WhatsApp as WhatsAppIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { api } from '../../utils/api';
import {
  openWhatsAppWithTemplate,
  TEMPLATE_TYPES,
  buildSaleReviewTemplateData
} from '../../utils/whatsappTemplates';
import {
  getTaxableAmount,
  getVatAmount,
  accountIncludesVat,
  getMarginWithVat
} from '../../utils/accountingAmounts';
import SaleEditDialog from './SaleEditDialog';

const SalesHistory = () => {
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSale, setSelectedSale] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [refundDialogOpen, setRefundDialogOpen] = useState(false);
  const [saleToRefund, setSaleToRefund] = useState(null);
  const [visibleMargins, setVisibleMargins] = useState({});
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saleToEdit, setSaleToEdit] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [accounts, setAccounts] = useState([]);
  
  // Filtri
  const [filters, setFilters] = useState({
    startDate: null,
    endDate: null,
    customerName: '',
    minAmount: '',
    maxAmount: '',
    saleId: ''
  });

  useEffect(() => {
    fetchSales();
    fetchPaymentMethods();
    fetchAccounts();
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchSales();
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [filters]);

  // Funzione di utilità per impostare le date a inizio e fine giornata
  const setDateToStartOfDay = (date) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate;
  };

  const setDateToEndOfDay = (date) => {
    if (!date) return null;
    const newDate = new Date(date);
    newDate.setHours(23, 59, 59, 999);
    return newDate;
  };

  // Handler per i cambiamenti di data
  const handleStartDateChange = (newValue) => {
    setFilters(prev => ({ 
      ...prev, 
      startDate: newValue ? setDateToStartOfDay(newValue) : null 
    }));
  };

  const handleEndDateChange = (newValue) => {
    setFilters(prev => ({ 
      ...prev, 
      endDate: newValue ? setDateToEndOfDay(newValue) : null 
    }));
  };

  const fetchSales = async () => {
    try {
      setLoading(true);
      setError(null);

      // Costruisci i parametri della query
      const queryParams = new URLSearchParams();
      
      if (filters.startDate) {
        try {
          // Format solo la data, non l'ora
          const formattedDate = format(filters.startDate, 'yyyy-MM-dd');
          queryParams.append('startDate', formattedDate);
        } catch (error) {
          console.error('Errore nella formattazione della data di inizio:', error);
        }
      }
      if (filters.endDate) {
        try {
          // Aggiungiamo un giorno alla data di fine per includere tutte le vendite dell'ultimo giorno
          const nextDay = new Date(filters.endDate);
          nextDay.setDate(nextDay.getDate() + 1);
          const formattedDate = format(nextDay, 'yyyy-MM-dd');
          queryParams.append('endDate', formattedDate);
        } catch (error) {
          console.error('Errore nella formattazione della data di fine:', error);
        }
      }
      
      // Non inviamo questi parametri al backend poiché causano errori 500
      // Faremo il filtraggio lato client
      
      const response = await api.get(`/sales?${queryParams.toString()}`);
      let salesData = response.data;
      
      // Filtra le vendite per escludere quelle con stato 'draft'
      salesData = salesData.filter(sale => sale.status !== 'draft');
      
      // Calcola il totale per ogni vendita se non è già presente
      salesData = salesData.map(sale => {
        // Se la vendita non ha il totalAmount, calcolalo dagli items
        if (!sale.totalAmount && sale.items && sale.items.length > 0) {
          const calculatedTotal = sale.items.reduce(
            (sum, item) => sum + (parseFloat(item.price || 0) * (item.quantity || 0)), 
            0
          );
          return {
            ...sale,
            totalAmount: calculatedTotal.toFixed(2)
          };
        }
        return sale;
      });
      
      // Filtraggio lato client
      // Filtro per nome cliente o telefono
      if (filters.customerName && filters.customerName.trim()) {
        const searchTerm = filters.customerName.trim().toLowerCase();
        salesData = salesData.filter(sale => {
          if (!sale.Customer) return false;
          const customerName = `${sale.Customer.firstName || ''} ${sale.Customer.lastName || ''}`.toLowerCase();
          const customerPhone = (sale.Customer.phone || '').toLowerCase();
          return customerName.includes(searchTerm) || customerPhone.includes(searchTerm);
        });
      }

      // Filtro per importo minimo
      if (filters.minAmount && !isNaN(parseFloat(filters.minAmount))) {
        const minAmount = parseFloat(filters.minAmount);
        salesData = salesData.filter(sale => {
          const total = parseFloat(sale.totalAmount || 0);
          return total >= minAmount;
        });
      }
      
      // Filtro per importo massimo
      if (filters.maxAmount && !isNaN(parseFloat(filters.maxAmount))) {
        const maxAmount = parseFloat(filters.maxAmount);
        salesData = salesData.filter(sale => {
          const total = parseFloat(sale.totalAmount || 0);
          return total <= maxAmount;
        });
      }
      
      // Filtra per ID vendita se specificato
      if (filters.saleId && filters.saleId.trim()) {
        const searchId = filters.saleId.trim();
        salesData = salesData.filter(sale => {
          return sale.id.toString() === searchId;
        });
      }
      
      setSales(salesData);
      
    } catch (error) {
      console.error('Errore nel caricamento delle vendite:', error);
      setError('Si è verificato un errore nel caricamento delle vendite');
      setSales([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await api.get('/payment-methods');
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei metodi di pagamento:', error);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/accounts');
      setAccounts(response.data);
    } catch (error) {
      console.error('Errore nel caricamento dei conti:', error);
    }
  };

  const getPaymentMethodName = (id) => {
    if (!id) return 'N/A';
    const method = paymentMethods.find(m => m.id === id);
    return method ? method.name : `ID: ${id}`;
  };

  const getAccountName = (id) => {
    if (!id) return 'N/A';
    const account = accounts.find(a => a.id === id);
    return account ? account.name : `ID: ${id}`;
  };

  const handleSaleClick = async (saleId) => {
    try {
      setLoading(true);
      const response = await api.get(`/sales/${saleId}`);
      
      // Calcola il totalAmount se non è presente
      const saleData = response.data;
      if (!saleData.totalAmount && saleData.items && saleData.items.length > 0) {
        const calculatedTotal = saleData.items.reduce(
          (sum, item) => sum + (parseFloat(item.price) * item.quantity), 
          0
        );
        saleData.totalAmount = calculatedTotal.toFixed(2);
      }
      
      setSelectedSale(saleData);
      setOpenDialog(true);
    } catch (error) {
      console.error('Errore nel caricamento dei dettagli della vendita:', error);
      setError('Si è verificato un errore nel caricamento dei dettagli della vendita');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedSale(null);
  };

  const handleEditClick = async (sale, event) => {
    if (event) event.stopPropagation();
    try {
      setLoading(true);
      const response = await api.get(`/sales/${sale.id}`);
      const saleData = response.data;
      if (!saleData.totalAmount && saleData.items?.length) {
        saleData.totalAmount = saleData.items.reduce(
          (sum, item) => sum + (parseFloat(item.price) * item.quantity),
          0
        ).toFixed(2);
      }
      saleData.Customer = saleData.Customer || saleData.customer;
      setSaleToEdit(saleData);
      setEditDialogOpen(true);
      setOpenDialog(false);
    } catch (error) {
      console.error('Errore nel caricamento della vendita da modificare:', error);
      setError('Errore nel caricamento della vendita da modificare');
    } finally {
      setLoading(false);
    }
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setSaleToEdit(null);
  };

  const handleSaleSaved = (updatedSale) => {
    const normalizedSale = {
      ...updatedSale,
      Customer: updatedSale.Customer || updatedSale.customer
    };
    setSales((prev) => prev.map((sale) => (
      sale.id === normalizedSale.id ? { ...sale, ...normalizedSale } : sale
    )));
    setSelectedSale((prev) => (prev?.id === normalizedSale.id ? normalizedSale : prev));
    setError(null);
  };

  const handleOpenReviewWhatsApp = async (sale) => {
    try {
      let s = sale;
      if (!s?.Customer?.phone && s?.id) {
        const res = await api.get(`/sales/${s.id}`);
        s = res.data;
      }
      if (!s?.Customer?.phone) {
        setError('Numero di telefono del cliente non disponibile per WhatsApp.');
        return;
      }
      const gsRes = await api.get('/settings').catch(() => ({ data: {} }));
      const templateData = buildSaleReviewTemplateData(s, gsRes.data || {});
      await openWhatsAppWithTemplate(
        s.Customer.phone,
        TEMPLATE_TYPES.MAINTENANCE_REVIEW,
        templateData,
        s.Customer.countryCode
      );
    } catch (err) {
      console.error('Errore apertura WhatsApp recensioni:', err);
      setError('Impossibile aprire WhatsApp. Riprova.');
    }
  };

  const handleResetFilters = () => {
    setFilters({
      startDate: null,
      endDate: null,
      customerName: '',
      minAmount: '',
      maxAmount: '',
      saleId: ''
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'draft':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'completed':
        return 'Completata';
      case 'pending':
        return 'In attesa';
      case 'cancelled':
        return 'Annullata';
      case 'draft':
        return 'Bozza';
      default:
        return 'Sconosciuto';
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const toggleMargin = (saleId) => {
    setVisibleMargins((prev) => ({ ...prev, [saleId]: !prev[saleId] }));
  };

  const handleRefundClick = (sale) => {
    setSaleToRefund(sale);
    setRefundDialogOpen(true);
  };

  const handleRefundConfirm = async () => {
    if (!saleToRefund) return;
    try {
      setLoading(true);
      setError(null);
      await api.post(`/sales/${saleToRefund.id}/refund`);
      setRefundDialogOpen(false);
      setSaleToRefund(null);
      if (selectedSale?.id === saleToRefund.id) {
        handleCloseDialog();
      }
      fetchSales();
    } catch (err) {
      console.error('Errore nel rimborso della vendita:', err);
      setError(err.response?.data?.message || 'Si è verificato un errore nel rimborso della vendita');
      setRefundDialogOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      setLoading(true);
      await api.delete(`/sales/${selectedSale.id}`);
      setDeleteDialogOpen(false);
      handleCloseDialog();
      fetchSales();
    } catch (error) {
      console.error('Errore nell\'eliminazione della vendita:', error);
      setError('Si è verificato un errore nell\'eliminazione della vendita');
    } finally {
      setLoading(false);
    }
  };

  const getSaleAccount = (sale) => accounts.find((account) => account.id === sale.accountId);

  const isCustomSaleItem = (item) => {
    const productCode = item.Product?.code || '';
    return (
      item.isCustom ||
      productCode === 'ART-CUST' ||
      productCode.startsWith('ART-CUST-') ||
      (typeof item.productId === 'string' && item.productId.startsWith('ART-PER-'))
    );
  };

  const getSaleItemUnitCost = (item) => {
    const fromLine = parseFloat(item.purchasePrice);
    if (Number.isFinite(fromLine) && fromLine > 0) return fromLine;
    if (isCustomSaleItem(item)) return fromLine || 0;
    return parseFloat(item.Product?.purchasePrice || 0) || 0;
  };

  const getSaleItemsCostNoVat = (sale) => {
    if (!sale?.items?.length) return 0;
    return sale.items.reduce((sum, item) => {
      const quantity = parseInt(item.quantity || 0, 10);
      return sum + getSaleItemUnitCost(item) * quantity;
    }, 0);
  };

  const getSaleFinancialBreakdown = (sale) => {
    const paymentAmount = parseFloat(sale?.paymentAmount || sale?.totalAmount || 0);
    const account = getSaleAccount(sale);
    const includesVat = accountIncludesVat(account);
    const totalCostNoVAT = getSaleItemsCostNoVat(sale);
    const totalCostWithVAT = totalCostNoVAT * 1.22;
    const paymentAmountNoVAT = getTaxableAmount(paymentAmount, includesVat);
    const vatOnPayment = getVatAmount(paymentAmount, includesVat);
    const marginNoVAT = paymentAmountNoVAT - totalCostNoVAT;
    const marginWithVAT = getMarginWithVat(paymentAmount, totalCostWithVAT, paymentAmountNoVAT, includesVat);

    return {
      paymentAmount,
      paymentAmountNoVAT,
      vatOnPayment,
      totalCostNoVAT,
      totalCostWithVAT,
      marginNoVAT,
      marginWithVAT,
      includesVat
    };
  };

  // Calcola il margine per una vendita (con IVA inclusa)
  const calculateMargin = (sale) => {
    if (!sale || !sale.items || !Array.isArray(sale.items) || sale.items.length === 0) {
      return { value: 0, percentage: 0 };
    }

    const breakdown = getSaleFinancialBreakdown(sale);
    const marginValue = breakdown.includesVat ? breakdown.marginWithVAT : breakdown.marginNoVAT;
    const marginBase = breakdown.includesVat ? breakdown.paymentAmount : breakdown.paymentAmountNoVAT;
    const marginPercentage = marginBase > 0 ? (marginValue / marginBase) * 100 : 0;

    return {
      value: marginValue,
      percentage: marginPercentage
    };
  };

  // Formatta il margine per la visualizzazione
  const formatMargin = (margin) => {
    // Verifica se margin è un numero o un oggetto
    const marginValue = typeof margin === 'object' ? margin.value : margin;
    const marginPercentage = typeof margin === 'object' ? margin.percentage : 0;
    
    if (isNaN(marginValue)) return '-';
    
    const formattedValue = parseFloat(marginValue).toFixed(2);
    const isPositive = marginValue > 0;
    
    return (
      <Box sx={{ 
        color: isPositive ? 'success.main' : 'error.main',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography variant="body2">
          {isPositive ? '+' : ''}{formattedValue}€
        </Typography>
        <Typography variant="caption">
          ({marginPercentage.toFixed(1)}%)
        </Typography>
      </Box>
    );
  };

  // Funzione per formattare un importo con due decimali
  const formatAmount = (amount) => {
    return Number(parseFloat(amount) || 0).toFixed(2);
  };

  const [receiptNumbers, setReceiptNumbers] = useState({});

  // Funzione per generare un numero di ricevuta reale dal backend
  const generateRealReceiptNumber = async () => {
    try {
      const response = await api.get('/sales/generate-receipt-number');
      return response.data.receiptNumber;
    } catch (error) {
      console.error('Errore nella generazione del numero ricevuta:', error);
      // Fallback locale se il backend non risponde
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0];
      const counter = Math.floor(Date.now() / 1000) % 10000;
      return `${dateStr}-${counter.toString().padStart(4, '0')}`;
    }
  };

  // Determina il numero di ricevuta da mostrare
  const getReceiptNumber = (sale) => {
    if (sale?.saleNumber) {
      return `${sale.saleNumber} (ID: ${sale.id})`; // Mostra sia il numero reale che l'ID
    } else if (sale?.id) {
      return `RIC-${sale.id}`; // Fallback al vecchio formato se disponibile
    } else {
      return receiptNumbers[sale?.tempId] || 'Generando...'; // Usa il numero generato o mostra loading
    }
  };

  // Funzione per stampare la ricevuta della vendita
  const handlePrintReceipt = async (sale) => {
    if (!sale) return;

    try {
      // Prima recuperiamo i dati completi della vendita se il cliente non è presente
      let completeData = sale;
      
      if (!sale.Customer || !sale.Customer.phone) {
        console.log("Dati cliente mancanti, caricamento dettagli completi...");
        const saleResponse = await api.get(`/sales/${sale.id}`);
        completeData = saleResponse.data;
      }
      
      // Prima recuperiamo le impostazioni aziendali dal server
      const response = await api.get('/company-settings');
      
      // Otteniamo il metodo di pagamento
      const paymentMethod = paymentMethods.find(m => m.id === completeData.paymentMethodId);

      // Prepariamo i dati per la ricevuta con i dati aziendali
      const companySettings = {
        companyName: response.data?.companyName || 'FreeWheel',
        companyAddress: response.data?.companyAddress || '',
        companyCity: response.data?.companyCity || '',
        companyPostalCode: response.data?.companyPostalCode || '',
        companyProvince: response.data?.companyProvince || '',
        companyPhone: response.data?.companyPhone || '',
        companyEmail: response.data?.companyEmail || '',
        companyVatNumber: response.data?.companyVatNumber || ''
      };

      console.log("Impostazioni aziendali per la stampa:", companySettings);
      console.log("Dati cliente per la stampa:", completeData.Customer);

      // Calcola il totale della vendita
      const calculateTotal = (items) => {
        if (!items || !Array.isArray(items)) return 0;
        return items.reduce((total, item) => {
          const price = parseFloat(item.price || item.unitPrice || 0);
          const quantity = parseInt(item.quantity || 0);
          return total + (price * quantity);
        }, 0);
      };

      // Apriamo la finestra di stampa
      const printWindow = window.open('', 'PRINT', 'width=600,height=800');

      // Generiamo l'HTML della ricevuta
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Stampa Ricevuta</title>
            <meta charset="utf-8">
            <style>
              @page { 
                size: A5; 
                margin: 10mm;
              }
              body { 
                margin: 0; 
                padding: 0;
                font-family: Arial, Helvetica, sans-serif;
                color: #333;
              }
              .receipt-container {
                width: 148mm;
                padding: 10mm;
                box-sizing: border-box;
                background-color: white;
              }
              .receipt-page {
                width: 100%;
                page-break-after: always;
                break-inside: avoid;
                display: flex;
                flex-direction: column;
              }
              .receipt-page:last-child {
                page-break-after: auto;
              }
              .receipt-header {
                display: flex;
                justify-content: space-between;
                align-items: flex-start;
                margin-bottom: 1.5mm;
              }
              .company-info {
                max-width: 100%;
              }
              .company-name {
                font-size: 14pt;
                font-weight: bold;
                color: #DB1111;
                margin-bottom: 1mm;
                word-break: break-word;
              }
              .company-details {
                font-size: 8pt;
                color: #666;
                line-height: 1.1;
              }
              .receipt-title {
                text-align: center;
                font-size: 13pt;
                font-weight: bold;
                margin: 1.5mm 0;
                margin-top: 15mm;
                color: #333;
                border-bottom: 2px solid #DB1111;
                padding-bottom: 0.5mm;
              }
              .customer-info {
                display: grid;
                grid-template-columns: 1fr 1fr;
                gap: 1.5mm;
                margin-bottom: 1.5mm;
                background-color: #f8f8f8;
                padding: 1.5mm;
                border-radius: 2px;
              }
              .info-label {
                font-size: 8pt;
                color: #666;
                margin-bottom: 0.5mm;
              }
              .info-value {
                font-weight: bold;
                font-size: 9pt;
              }
              .products-table {
                width: 100%;
                border-collapse: collapse;
                font-size: 9pt;
                margin-bottom: 4mm;
              }
              .products-table th, .products-table td {
                padding: 0.5mm 1mm;
                border-bottom: 1px solid #eee;
              }
              .products-table th {
                text-align: left;
              }
              .products-table td.price, .products-table th.price {
                text-align: right;
              }
              .products-table td.quantity, .products-table th.quantity {
                text-align: center;
              }
              .section-title {
                font-weight: bold;
                margin-bottom: 0.5mm;
                color: #DB1111;
                font-size: 10pt;
              }
              .payment-details {
                padding: 1.5mm;
                border: 1px solid #eee;
                border-radius: 2px;
                background-color: #f8f8f8;
                margin-bottom: 3mm;
              }
              .page-number {
                position: absolute;
                bottom: 1mm;
                right: 10mm;
                font-size: 8pt;
                color: #888;
              }
              .thank-you {
                width: 100%;
                text-align: center;
                margin-top: 5mm;
                font-size: 10pt;
                color: #333;
                font-weight: bold;
              }
            </style>
          </head>
          <body>
            <div class="receipt-container">
              ${generateReceiptHtml(completeData, paymentMethod, companySettings)}
            </div>
          </body>
        </html>
      `);
      
      printWindow.document.close();
      printWindow.focus();
      
      // Diamo al browser il tempo di renderizzare il contenuto prima della stampa
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    } catch (error) {
      console.error("Errore durante la stampa della ricevuta:", error);
      alert("Si è verificato un errore durante la stampa della ricevuta. Controllare la console per i dettagli.");
    }
  };

  // Funzione per generare l'HTML della ricevuta
  const generateReceiptHtml = (sale, paymentMethod, companySettings) => {
    const items = sale?.items || sale?.SaleItems || [];
    const totalPages = Math.ceil(items.length / 16) || 1;
    
    // Formatta la data
    const paymentDate = sale.paymentDate 
      ? new Date(sale.paymentDate).toLocaleDateString('it-IT')
      : new Date().toLocaleDateString('it-IT');
      
    // Nome metodo di pagamento
    const paymentMethodName = paymentMethod?.name || 
      (sale?.PaymentMethod ? sale.PaymentMethod.name : 
        (sale?.paymentMethodId ? 'Metodo ID: ' + sale.paymentMethodId : '-'));
    
    // Calcolo del totale
    const total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.price || item.unitPrice || 0) * parseInt(item.quantity || 0));
    }, 0);

    let html = '';
    
    // Dividi gli articoli in pagine
    for (let i = 0; i < totalPages; i++) {
      const pageItems = items.slice(i * 16, (i + 1) * 16);
      const isLastPage = i === totalPages - 1;
      
      html += `
        <div class="receipt-page">
          <!-- Intestazione -->
          <div class="company-info">
            <div class="company-name">${companySettings.companyName || 'FreeWheel'}</div>
            <div class="company-details">
              ${companySettings.companyAddress ? `<div>${companySettings.companyAddress}</div>` : ''}
              ${companySettings.companyCity ? 
                `<div>${companySettings.companyCity}${companySettings.companyPostalCode ? ' ' + companySettings.companyPostalCode : ''}${companySettings.companyProvince ? ' (' + companySettings.companyProvince + ')' : ''}</div>` 
                : ''}
              ${companySettings.companyPhone ? `<div>Tel: ${companySettings.companyPhone}</div>` : ''}
              ${companySettings.companyEmail ? `<div>Email: ${companySettings.companyEmail}</div>` : ''}
              ${companySettings.companyVatNumber ? `<div>P.IVA: ${companySettings.companyVatNumber}</div>` : ''}
            </div>
          </div>
          
          <!-- Titolo -->
          <div class="receipt-title">RICEVUTA DI VENDITA</div>
          
          <!-- Info cliente -->
          <div class="customer-info">
            <div>
              <div class="info-label">Cliente</div>
              <div class="info-value">${sale?.Customer ? `${sale.Customer.firstName} ${sale.Customer.lastName}` : ''}</div>
            </div>
            <div>
              <div class="info-label">Telefono</div>
              <div class="info-value">${sale?.Customer?.phone || ''}</div>
            </div>
            <div>
              <div class="info-label">Data Pagamento</div>
              <div class="info-value">${paymentDate}</div>
            </div>
            <div>
              <div class="info-label">Numero Ricevuta</div>
              <div class="info-value">${getReceiptNumber(sale)}</div>
            </div>
          </div>
          
          <!-- Articoli -->
          ${pageItems.length > 0 ? `
            <div>
              <div class="section-title">PRODOTTI ACQUISTATI</div>
              <table class="products-table">
                <thead>
                  <tr>
                    <th>Prodotto</th>
                    <th class="quantity">Quantità</th>
                    <th class="price">Prezzo Unitario</th>
                    <th class="price">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  ${pageItems.map((item, idx) => `
                    <tr>
                      <td>${item.description || 'Senza descrizione'}</td>
                      <td class="quantity">${item.quantity}</td>
                      <td class="price">€${formatAmount(item.unitPrice || item.price || 0)}</td>
                      <td class="price">€${formatAmount((item.unitPrice || item.price || 0) * item.quantity)}</td>
                    </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          ` : ''}
          
          <!-- Dettagli pagamento solo nell'ultima pagina -->
          ${isLastPage ? `
            <div>
              <div class="section-title">DETTAGLI PAGAMENTO</div>
              <div class="payment-details">
                <table class="products-table">
                  <tbody>
                    <tr>
                      <td>Totale vendita</td>
                      <td class="price">€${formatAmount(total)}</td>
                    </tr>
                    <tr>
                      <td>Importo pagato</td>
                      <td class="price">€${formatAmount(sale.paymentAmount || 0)}</td>
                    </tr>
                    <tr>
                      <td>Metodo di pagamento</td>
                      <td class="price">${paymentMethodName}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ` : ''}
          
          <!-- Numero pagina -->
          <div class="page-number">Pagina ${i + 1} / ${totalPages}</div>
          
          <!-- Ringraziamento solo nell'ultima pagina -->
          ${isLastPage ? `
            <div class="thank-you">
              Grazie per aver scelto i nostri servizi!<br>
              Lo staff di ${companySettings.companyName || 'FreeWheel'}
            </div>
          ` : ''}
        </div>
      `;
    }
    
    return html;
  };

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        Storico Vendite
      </Typography>

      {/* Filtri */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
              <FilterIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Filtri</Typography>
              <Button
            variant="outlined" 
            size="small" 
                startIcon={<ClearIcon />}
                onClick={handleResetFilters}
            sx={{ ml: 'auto' }}
              >
                Reset
              </Button>
            </Box>
          
        <Grid container spacing={2}>
          <Grid item xs={12} md={6} lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
              <DatePicker
                label="Data inizio"
                value={filters.startDate}
                onChange={handleStartDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6} lg={3}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={it}>
              <DatePicker
                label="Data fine"
                value={filters.endDate}
                onChange={handleEndDateChange}
                renderInput={(params) => <TextField {...params} fullWidth />}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} md={6} lg={2}>
            <TextField
              label="Cliente"
              value={filters.customerName}
              onChange={(e) => setFilters({...filters, customerName: e.target.value})}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={6} lg={1}>
            <TextField
              label="ID"
              value={filters.saleId}
              onChange={(e) => setFilters({...filters, saleId: e.target.value})}
              fullWidth
              type="number"
            />
          </Grid>
          <Grid item xs={12} md={6} lg={1.5}>
            <TextField
              label="Importo min"
              value={filters.minAmount}
              onChange={(e) => setFilters({...filters, minAmount: e.target.value})}
              fullWidth
              type="number"
              InputProps={{ 
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12} md={6} lg={1.5}>
            <TextField
              label="Importo max"
              value={filters.maxAmount}
              onChange={(e) => setFilters({...filters, maxAmount: e.target.value})}
              fullWidth
              type="number"
              InputProps={{ 
                startAdornment: <InputAdornment position="start">€</InputAdornment>,
              }}
            />
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
                <TableCell>ID</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Cliente</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Pagamento</TableCell>
                <TableCell>Importo</TableCell>
                <TableCell>Margine</TableCell>
                <TableCell>Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
              {sales.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    Nessuna vendita trovata
                  </TableCell>
                </TableRow>
              ) : (
                sales.map((sale) => {
                  const margin = calculateMargin(sale);
                  return (
              <TableRow key={sale.id}>
                      <TableCell>{sale.id}</TableCell>
                <TableCell>
                        {new Date(sale.createdAt).toLocaleDateString('it-IT')}
                </TableCell>
                      <TableCell>
                        {sale.Customer ? `${sale.Customer.firstName} ${sale.Customer.lastName}` : 'N/A'}
                      </TableCell>
                      <TableCell>
                  <Chip
                    label={getStatusLabel(sale.status)}
                    color={getStatusColor(sale.status)}
                    size="small"
                  />
                </TableCell>
                      <TableCell>
                        {sale.paymentMethodId ? getPaymentMethodName(sale.paymentMethodId) : '-'}
                      </TableCell>
                      <TableCell>
                        €{parseFloat(sale.paymentAmount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {visibleMargins[sale.id] ? formatMargin(margin) : null}
                          <Tooltip title={visibleMargins[sale.id] ? 'Nascondi margine' : 'Mostra margine'}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMargin(sale.id);
                              }}
                            >
                              {visibleMargins[sale.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                  <IconButton
                          size="small" 
                    onClick={() => handleSaleClick(sale.id)}
                          color="primary"
                  >
                    <InfoIcon />
                  </IconButton>
                        <Tooltip title="Modifica vendita">
                          <IconButton
                            size="small"
                            color="primary"
                            disabled={sale.status !== 'completed'}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditClick(sale, e);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="WhatsApp richiesta recensione">
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={!sale.Customer}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenReviewWhatsApp(sale);
                              }}
                            >
                              <WhatsAppIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Button
                          size="small"
                          color="warning"
                          variant="outlined"
                          startIcon={<UndoIcon />}
                          disabled={sale.status !== 'completed'}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefundClick(sale);
                          }}
                          sx={{ mr: 0.5 }}
                        >
                          Rimborso
                        </Button>
                        <IconButton 
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedSale(sale);
                            setDeleteDialogOpen(true);
                          }}
                        >
                          <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
                  );
                })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      )}

      {/* Dialog Dettagli Vendita */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography variant="h6" component="span">Dettagli Vendita</Typography>
            {selectedSale && (
              <Typography variant="subtitle2" color="text.secondary" component="span">
                {format(new Date(selectedSale.createdAt), 'dd/MM/yyyy HH:mm')}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedSale && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1" gutterBottom>
                  Cliente: {selectedSale.Customer ? `${selectedSale.Customer.firstName} ${selectedSale.Customer.lastName}` : 'Cliente sconosciuto'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Chip
                    label={getStatusLabel(selectedSale.status)}
                    color={getStatusColor(selectedSale.status)}
                    size="small"
                  />
                  
                  {selectedSale.status === 'completed' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Pagato il: {selectedSale.paymentDate ? format(new Date(selectedSale.paymentDate), 'dd/MM/yyyy') : 'N/A'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Importo: €{selectedSale.paymentAmount ? parseFloat(selectedSale.paymentAmount).toFixed(2) : '0.00'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Metodo: {getPaymentMethodName(selectedSale.paymentMethodId)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Conto: {getAccountName(selectedSale.accountId)}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Grid>
              
              {selectedSale.notes && (
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>
                    Note: {selectedSale.notes}
                  </Typography>
                </Grid>
              )}
              
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Articoli
                </Typography>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Prodotto</TableCell>
                        <TableCell align="center">Quantità</TableCell>
                        <TableCell align="right">Prezzo Vendita</TableCell>
                        <TableCell align="right">Totale</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedSale.items && selectedSale.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            <Typography variant="body2">
                              {item.description || 'Senza descrizione'}
                            </Typography>
                            {item.includeLabor && (
                              <Typography variant="caption" color="text.secondary">
                                Include manodopera
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">€{parseFloat(item.price || 0).toFixed(2)}</TableCell>
                          <TableCell align="right">€{(parseFloat(item.price || 0) * item.quantity).toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                      {(!selectedSale.items || selectedSale.items.length === 0) && (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography variant="body2" color="text.secondary">
                              Nessun articolo presente
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>

              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Typography variant="h6">
                    Totale: €{selectedSale.totalAmount ? parseFloat(selectedSale.totalAmount).toFixed(2) : '0.00'}
                  </Typography>
                </Box>
                
                {/* Riepilogo finanziario dettagliato */}
                <Box sx={{ 
                  p: 2, 
                  mt: 2, 
                  border: '1px solid #e0e0e0', 
                  borderRadius: 1,
                  bgcolor: '#f9f9f9'
                }}>
                  <Typography variant="subtitle1" gutterBottom fontWeight="bold">
                    Riepilogo Finanziario
                  </Typography>
                  
                  {(() => {
                    const totalAmount = parseFloat(selectedSale.totalAmount || 0);
                    const paymentAmount = parseFloat(selectedSale.paymentAmount || 0);
                    const breakdown = getSaleFinancialBreakdown(selectedSale);
                    const {
                      paymentAmountNoVAT,
                      vatOnPayment,
                      totalCostNoVAT,
                      totalCostWithVAT,
                      marginNoVAT,
                      marginWithVAT,
                      includesVat
                    } = breakdown;
                    const vatOnCosts = totalCostNoVAT * 0.22;
                    const discount = totalAmount - paymentAmount;
                    const discountPercentage = totalAmount > 0 ? (discount / totalAmount) * 100 : 0;
                    const marginNoVATPercentage = paymentAmountNoVAT > 0 ? (marginNoVAT / paymentAmountNoVAT) * 100 : 0;
                    const marginWithVATBase = includesVat ? paymentAmount : paymentAmountNoVAT;
                    const marginWithVATPercentage = marginWithVATBase > 0 ? (marginWithVAT / marginWithVATBase) * 100 : 0;
                    
                    return (
                      <Grid container spacing={1}>
                        <Grid item xs={12}>
                          <Typography variant="body2" color="text.secondary">
                            Conto: {getAccountName(selectedSale.accountId)}
                            {' '}({includesVat ? 'importi con IVA inclusa' : 'importi imponibili / IVA esclusa'})
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Importo pagato:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>
                            Imponibile: €{paymentAmountNoVAT.toFixed(2)}
                          </Typography>
                          {includesVat && (
                            <Typography variant="body2" sx={{ pl: 2 }}>
                              IVA (22%): €{vatOnPayment.toFixed(2)}
                            </Typography>
                          )}
                          <Typography variant="body2" sx={{ pl: 2, fontWeight: 'bold' }}>
                            Totale registrato: €{paymentAmount.toFixed(2)}
                          </Typography>
                        </Grid>
                        
                        {discount > 0 && (
                          <Grid item xs={12}>
                            <Typography variant="body2">
                              <strong>Sconto applicato:</strong> €{discount.toFixed(2)} ({discountPercentage.toFixed(2)}%)
                            </Typography>
                          </Grid>
                        )}
                        
                        <Grid item xs={12}>
                          <Typography variant="body2">
                            <strong>Totale costi:</strong>
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>
                            Senza IVA: €{totalCostNoVAT.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 2 }}>
                            IVA (22%): €{vatOnCosts.toFixed(2)}
                          </Typography>
                          <Typography variant="body2" sx={{ pl: 2, fontWeight: 'bold' }}>
                            Con IVA: €{totalCostWithVAT.toFixed(2)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              Margine
                            </Typography>
                            <Tooltip title={visibleMargins[selectedSale.id] ? 'Nascondi margine' : 'Mostra margine'}>
                              <IconButton size="small" onClick={() => toggleMargin(selectedSale.id)}>
                                {visibleMargins[selectedSale.id] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            </Tooltip>
                          </Box>
                          {visibleMargins[selectedSale.id] && (
                          <>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              color: marginNoVAT >= 0 ? 'success.main' : 'error.main',
                              fontWeight: 'bold'
                            }}
                          >
                            <strong>Margine:</strong>
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              pl: 2,
                              color: marginNoVAT >= 0 ? 'success.main' : 'error.main',
                              fontWeight: includesVat ? 'normal' : 'bold'
                            }}
                          >
                            Senza IVA: €{marginNoVAT.toFixed(2)} ({marginNoVATPercentage.toFixed(2)}%)
                          </Typography>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              pl: 2,
                              color: marginWithVAT >= 0 ? 'success.main' : 'error.main',
                              fontWeight: includesVat ? 'bold' : 'normal'
                            }}
                          >
                            {includesVat ? 'Con IVA' : 'Al netto IVA sui costi'}: €{marginWithVAT.toFixed(2)} ({marginWithVATPercentage.toFixed(2)}%)
                          </Typography>
                          </>
                          )}
                        </Grid>
                      </Grid>
                    );
                  })()}
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {selectedSale && selectedSale.status !== 'cancelled' && (
            <>
              {selectedSale.status === 'completed' && (
                <Button
                  variant="contained"
                  onClick={() => handleEditClick(selectedSale)}
                  startIcon={<EditIcon />}
                >
                  Modifica
                </Button>
              )}
              {selectedSale.status === 'completed' && (
                <Button
                  color="warning"
                  onClick={() => handleRefundClick(selectedSale)}
                  startIcon={<UndoIcon />}
                >
                  Rimborso
                </Button>
              )}
              <Button
                color="primary"
                onClick={() => handlePrintReceipt(selectedSale)}
                startIcon={<PrintIcon />}
              >
                Stampa Ricevuta
              </Button>
              <Button
                color="success"
                onClick={() => handleOpenReviewWhatsApp(selectedSale)}
                startIcon={<WhatsAppIcon />}
                disabled={!selectedSale.Customer}
              >
                Recensione WhatsApp
              </Button>
            <Button
              color="error"
              onClick={handleDeleteClick}
              startIcon={<DeleteIcon />}
            >
              Elimina
            </Button>
            </>
          )}
          <Button onClick={handleCloseDialog}>Chiudi</Button>
        </DialogActions>
      </Dialog>

      <SaleEditDialog
        open={editDialogOpen}
        sale={saleToEdit}
        paymentMethods={paymentMethods}
        accounts={accounts}
        onClose={handleEditClose}
        onSaved={handleSaleSaved}
      />

      <Dialog
        open={refundDialogOpen}
        onClose={() => setRefundDialogOpen(false)}
      >
        <DialogTitle>Conferma rimborso</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Rimborsare la vendita #{saleToRefund?.id}? La vendita viene annullata, gli articoli tornano in magazzino
            e viene registrata un&apos;uscita di prima nota di €{parseFloat(saleToRefund?.paymentAmount || 0).toFixed(2)} con lo stesso metodo di pagamento e lo stesso conto dell&apos;incasso.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundDialogOpen(false)}>Annulla</Button>
          <Button color="warning" onClick={handleRefundConfirm} autoFocus>
            Rimborsa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Conferma Eliminazione */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare questa vendita? Questa azione non può essere annullata.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Annulla</Button>
          <Button color="error" onClick={handleDeleteConfirm} autoFocus>
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SalesHistory; 